import type { User } from '@c214/shared';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { apiPost } from '../lib/api';

export const AUTH_QUERY_KEY = ['auth', 'me'] as const;

async function fetchCurrentUser(): Promise<User | null> {
  const response = await fetch('/api/auth/me', { credentials: 'include' });
  if (response.status === 401) return null;
  if (!response.ok) throw new Error(`Не вдалося перевірити сесію (${response.status})`);
  return (await response.json()) as User;
}

export function useAuth() {
  return useQuery({ queryKey: AUTH_QUERY_KEY, queryFn: fetchCurrentUser });
}

export function useLogin() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: { email: string; password: string }) =>
      apiPost<User>('/api/auth/login', input),
    onSuccess: (user) => queryClient.setQueryData(AUTH_QUERY_KEY, user),
  });
}

export function useRegister() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: { email: string; password: string; name?: string; phone?: string }) =>
      apiPost<User>('/api/auth/register', input),
    onSuccess: (user) => queryClient.setQueryData(AUTH_QUERY_KEY, user),
  });
}

export function useLogout() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: () => apiPost<void>('/api/auth/logout'),
    onSuccess: () => {
      // Update the auth query in place first — this notifies the already-mounted
      // Header observer correctly. `queryClient.clear()` here would instead REMOVE
      // the auth query entirely; the very next `setQueryData` call then has to build
      // a brand-new Query object for that key, which existing observers don't
      // reliably pick up in the same render pass (confirmed via e2e: the header's
      // profile icon kept showing the logged-in state after logout). Only the
      // other user-specific caches need clearing, and it's safe to do that after —
      // `useOrders`/`useSubscription` are `enabled: !!user`, so once `user` is
      // `null` those queries are already disabled and removing them has no
      // observer to race with.
      queryClient.setQueryData(AUTH_QUERY_KEY, null);
      queryClient.removeQueries({ queryKey: ['orders'] });
      queryClient.removeQueries({ queryKey: ['subscription'] });
      queryClient.removeQueries({ queryKey: ['admin'] });
      queryClient.removeQueries({ queryKey: ['notifications'] });
    },
  });
}
